import * as React from "react";
import { Card, CardContent } from "../../components/ui/card.tsx";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs.tsx";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import { useQuery } from "@tanstack/react-query";
import { ChartPie } from "lucide-react";
import { getEventAttendance } from "../../api/attendance/attendanceApi";

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center h-[300px] text-center p-6">
    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
      <ChartPie className="w-8 h-8 text-purple-600" />
    </div>
    <h3 className="text-lg font-medium mb-2">No Attendance Data</h3>
    <p className="text-sm text-gray-500 max-w-[250px]">
      Start recording attendance to see insights here
    </p>
  </div>
);

const LoadingState = () => (
  <div className="flex items-center justify-center h-[300px]">
    <div className="w-8 h-8 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
  </div>
);

const formatAttendanceData = (rawData) => {
  if (!rawData) return [];

  // Group data by month and calculate percentages
  const monthlyData = {};
  rawData.forEach((record) => {
    const month = new Date(record.date).toLocaleString("default", {
      month: "short",
    });
    if (!monthlyData[month]) {
      monthlyData[month] = { total: 0, present: 0 };
    }
    monthlyData[month].total++;
    if (record.status === "Full day" || record.status === "Half day") {
      monthlyData[month].present++;
    }
  });

  // Convert to chart format
  return Object.entries(monthlyData).map(([month, data]) => ({
    month,
    thisYear: Math.round((data.present / data.total) * 100),
    today: data.present > 0 ? Math.round((data.present / data.total) * 100) : 0,
  }));
};

export default function AttendanceChart() {
  const [isMobile, setIsMobile] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState("student");

  const { data: studentAttendance, isLoading: studentLoading } = useQuery({
    queryKey: ["attendance", "student"],
    queryFn: () => getEventAttendance("student"),
    enabled: activeTab === "student",
  });

  const { data: employeeAttendance, isLoading: employeeLoading } = useQuery({
    queryKey: ["attendance", "employee"],
    queryFn: () => getEventAttendance("employee"),
    enabled: activeTab === "employee",
  });

  // Check if device is mobile
  React.useEffect(() => {
    const checkIfMobile = () => setIsMobile(window.innerWidth < 768);
    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  const data = React.useMemo(() => {
    const rawData = activeTab === "student" ? studentAttendance : employeeAttendance;
    return formatAttendanceData(rawData);
  }, [activeTab, studentAttendance, employeeAttendance]);

  const isLoading = activeTab === "student" ? studentLoading : employeeLoading;
  const isEmpty = !isLoading && (!data || data.length === 0);

  return (
    <Card className="w-full bg-white">
      <CardContent className={`${isMobile ? "pt-4 px-3" : "pt-6"}`}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`${isMobile ? "w-full" : "w-[400px]"} grid grid-cols-2 bg-transparent`}>
            <TabsTrigger
              value="student"
              className="text-xs sm:text-sm data-[state=active]:bg-transparent data-[state=active]:text-black data-[state=active]:font-medium"
            >
              Student Attendance
            </TabsTrigger>
            <TabsTrigger
              value="employee"
              className="text-xs sm:text-sm text-gray-400 data-[state=active]:bg-transparent data-[state=active]:text-black data-[state=active]:font-medium"
            >
              Employee Attendance
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {isLoading ? <LoadingState /> : isEmpty ? <EmptyState /> : (
          <>
            <div className="flex items-center justify-end space-x-4 sm:space-x-6 mb-4 sm:mb-6 mt-3 sm:mt-0">
              <div className="flex items-center space-x-1 sm:space-x-2">
                <div className="h-2 w-2 rounded-full bg-purple-400" />
                <span className="text-xs text-gray-600">This year</span>
              </div>
              <div className="flex items-center space-x-1 sm:space-x-2">
                <div className="h-2 w-2 rounded-full bg-gray-200" />
                <span className="text-xs text-gray-600">Today</span>
              </div>
            </div>

            <div className={`${isMobile ? "h-[220px]" : "h-[300px]"} w-full`}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={data}
                  margin={{
                    top: 5,
                    right: isMobile ? 0 : 10,
                    left: isMobile ? -25 : -20,
                    bottom: 0,
                  }}
                >
                  <defs>
                    <linearGradient
                      id="thisYearGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#E9D5FF" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#E9D5FF" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient
                      id="todayGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#E2E8F0" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#E2E8F0" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#94A3B8", fontSize: isMobile ? 10 : 12 }}
                    dy={10}
                    interval={isMobile ? 1 : 0}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#94A3B8", fontSize: isMobile ? 10 : 12 }}
                    dx={-10}
                    tickFormatter={(value) => `${value}${isMobile ? "" : "%"}`}
                    domain={[0, 100]}
                    ticks={isMobile ? [0, 50, 100] : [0, 30, 65, 100]}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="rounded-lg border bg-white px-3 py-2 shadow-md">
                            <p className="text-xs text-gray-600 mb-1">{label}</p>
                            {payload.map((entry, index) => (
                              <div key={index} className="flex items-center gap-2">
                                <div
                                  className="h-2 w-2 rounded-full"
                                  style={{ backgroundColor: entry.color }}
                                />
                                <span className="text-xs font-medium">
                                  {entry.value}%
                                </span>
                              </div>
                            ))}
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="thisYear"
                    stroke="#A855F7"
                    strokeWidth={2}
                    fill="url(#thisYearGradient)"
                    dot={false}
                    activeDot={{
                      r: isMobile ? 3 : 4,
                      fill: "#A855F7",
                      stroke: "#fff",
                      strokeWidth: 2,
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="today"
                    stroke="#CBD5E1"
                    strokeWidth={2}
                    strokeDasharray="4 4"
                    fill="url(#todayGradient)"
                    dot={false}
                    activeDot={{
                      r: isMobile ? 3 : 4,
                      fill: "#CBD5E1",
                      stroke: "#fff",
                      strokeWidth: 2,
                    }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
